import { IconCircle, IconFlame } from "@tabler/icons-react";
import Link from "next/link";
import { Img } from "@/components/image";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type { DietHubDietCategoryType } from "@/features/hubs/types/diet-hub.type";
import { strapiUrl } from "@/lib/strapi";
import { cn } from "@/lib/utils";

export const DietHubHeroCategory = ({
  category,
  isSelected,
}: {
  category: DietHubDietCategoryType | undefined;
  isSelected: boolean;
}) => {
  if (!category) {
    return null;
  }

  return (
    <div className={cn(!isSelected && "hidden")} aria-hidden={!isSelected}>
      <div className="flex flex-col gap-10 lg:flex-row">
        <div className="flex flex-1 flex-col rounded-2xl border border-transparent shadow-lg lg:self-start">
          <div className="flex flex-col items-center justify-center gap-6 rounded-t-2xl bg-brand-green2-300 p-6">
            <Badge
              variant="outline"
              className="border-transparent bg-brand-grey-light text-brand-grey5 text-sm lg:text-base"
            >
              <IconFlame className="size-4" stroke={1.5} />
              <span>{category.caloricity}</span>
            </Badge>

            {category.icon?.url ? (
              <div className="relative h-20 w-[88px]">
                <div className="absolute aspect-square h-full rounded-full bg-brand-green1-300" />
                <Img
                  src={strapiUrl(category.icon.url)}
                  alt={category.icon.alternativeText ?? ""}
                  width={category.icon.width}
                  height={category.icon.height}
                  className="absolute right-0 bottom-0 z-10 size-[90%] object-cover"
                />
              </div>
            ) : null}
          </div>
          <div className="flex flex-col gap-6 rounded-b-2xl bg-background p-6">
            <h3 className="text-center font-medium text-brand-green1-300 text-xl lg:text-2xl">
              {category.title}
            </h3>

            <div className="flex flex-wrap gap-2">
              {category.tags.map((tag) => (
                <Badge
                  key={tag.id}
                  variant="outline"
                  className="border-brand-grey1 px-2 font-normal text-brand-grey5 text-sm"
                >
                  {tag.title}
                </Badge>
              ))}
            </div>

            <p className="text-sm">{category.description}</p>
          </div>
        </div>

        <div className="flex flex-1 flex-col gap-6">
          <h3 className="font-medium text-brand-green1-300 text-lg">
            {category.benefitsTitle}
          </h3>
          <ul className="flex flex-col gap-y-4">
            {category.benefits.map((benefit) => (
              <li
                key={benefit.id}
                className="flex gap-x-4 rounded-xl border border-border p-4"
              >
                <IconCircle
                  className="size-4 pt-1.5"
                  stroke="currentColor"
                  fill="currentColor"
                />
                <span>{benefit.title}</span>
              </li>
            ))}
          </ul>
          <div className="flex flex-col gap-4 lg:flex-row">
            <Button
              variant="secondary"
              size="wide"
              className="text-center"
              asChild
            >
              <Link href={category.callback?.href || ""}>
                {category.callback?.label || ""}
              </Link>
            </Button>
            {category.articleLink ? (
              <Button
                variant="link"
                size="wide"
                className="ml-4 px-0 text-center"
                asChild
              >
                <Link href={category.articleLink?.href || ""}>
                  {category.articleLink?.label || ""}
                </Link>
              </Button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};
